import React, { useState } from 'react';
import {
  FileText,
  Plus,
  ArrowRight,
  MapPin,
  Globe,
  FolderTree,
  Search as SearchIcon,
  Trash2,
  Tag,
  Layers,
  ShieldCheck,
  Search
} from 'lucide-react';
import { ContentPlanningProject, AuditInstance, KeywordResearchProject } from '../../types';
import { Button } from '../UI/Button';
import { ContentPlanningStorageService } from '../../services/contentPlanningStorageService';

interface ContentPlanningProjectListProps {
  projects: ContentPlanningProject[];
  audits: AuditInstance[];
  kwProjects: KeywordResearchProject[];
  onSelectProject: (id: string) => void;
  onCreateProject: (
    name: string,
    url: string,
    location: string,
    category: string,
    linkedAuditId?: string,
    linkedKeywordId?: string,
    importedPages?: any[]
  ) => void;
  onDeleteProject: (id: string) => void;
}

export const ContentPlanningProjectList: React.FC<ContentPlanningProjectListProps> = ({
  projects,
  audits,
  kwProjects = [],
  onSelectProject,
  onCreateProject,
  onDeleteProject,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourceMode, setSourceMode] = useState<'keyword' | 'audit' | 'new'>('keyword');
  const [selectedKwId, setSelectedKwId] = useState<string>(kwProjects[0]?.id || '');
  const [selectedAuditId, setSelectedAuditId] = useState<string>(audits[0]?.id || '');

  // Form Fields
  const [clientName, setClientName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [targetedLocation, setTargetedLocation] = useState('');
  const [category, setCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const handleKwSelect = (kwId: string) => {
    setSelectedKwId(kwId);
    const found = kwProjects.find(p => p.id === kwId);
    if (found) {
      setClientName(found.clientName);
      setWebsiteUrl(found.websiteUrl);
      if (found.businessProfile?.primaryLocation) {
        setTargetedLocation(found.businessProfile.primaryLocation);
      }
      if (found.businessProfile?.mainServices && found.businessProfile.mainServices.length > 0) {
        setCategory(found.businessProfile.mainServices.join(', '));
      } else {
        setCategory('Service & E-commerce');
      }
    }
  };

  const handleAuditSelect = (auditId: string) => {
    setSelectedAuditId(auditId);
    const found = audits.find(a => a.id === auditId);
    if (found) {
      setClientName(found.clientName);
      setWebsiteUrl(found.websiteUrl);
      setTargetedLocation('Miami, FL');
      setCategory('Local Services');
    }
  };

  const handleOpenModal = () => {
    if (kwProjects.length > 0) {
      setSourceMode('keyword');
      handleKwSelect(kwProjects[0].id);
    } else if (audits.length > 0) {
      setSourceMode('audit');
      handleAuditSelect(audits[0].id);
    } else {
      setSourceMode('new');
      setClientName('');
      setWebsiteUrl('');
      setTargetedLocation('');
      setCategory('');
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientName.trim() || !websiteUrl.trim()) return;

    let linkedAuditId: string | undefined = undefined;
    let linkedKeywordId: string | undefined = undefined;
    let importedPages: any[] = [];

    if (sourceMode === 'keyword') {
      linkedKeywordId = selectedKwId;
      const foundKw = kwProjects.find(p => p.id === selectedKwId);
      if (foundKw) {
        importedPages = ContentPlanningStorageService.importPagesFromKeywordProject(foundKw);
      }
    } else if (sourceMode === 'audit') {
      linkedAuditId = selectedAuditId;
    }

    onCreateProject(
      clientName.trim(),
      websiteUrl.trim(),
      targetedLocation.trim() || 'Global / Remote',
      category.trim() || 'General Business',
      linkedAuditId,
      linkedKeywordId,
      importedPages
    );

    setClientName('');
    setWebsiteUrl('');
    setTargetedLocation('');
    setCategory('');
    setIsModalOpen(false);
  };

  // Aggregated Stats
  const totalProjects = projects.length;
  const totalPagesPlanned = projects.reduce((acc, p) => acc + (p.pages?.length || 0), 0);
  const totalKeywordsTracked = projects.reduce((acc, p) => {
    return acc + (p.pages || []).reduce((pAcc, page) => {
      let cnt = 0;
      if (page.primaryKeyword) cnt++;
      cnt += page.primaryKeywordVariations?.length || 0;
      if (page.secondaryKeyword) cnt++;
      cnt += page.secondaryKeywordVariations?.length || 0;
      if (page.tertiaryKeyword) cnt++;
      cnt += page.tertiaryKeywordVariations?.length || 0;
      return pAcc + cnt;
    }, 0);
  }, 0);

  const filteredProjects = projects.filter(p => {
    const q = searchQuery.toLowerCase();
    return (
      p.clientName.toLowerCase().includes(q) ||
      p.websiteUrl.toLowerCase().includes(q) ||
      p.targetedLocation.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q)
    );
  });

  return (
    <div className="animate-fade-in" style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px 80px 24px' }}>
      
      {/* 1. HERO HEADER */}
      <div
        style={{
          background: '#090e1a',
          border: '1px solid #1e293b',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '24px',
        }}
      >
        <div style={{ flex: '1 1 560px' }}>
          {/* Metadata Badges */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', fontWeight: 800, color: '#f43f5e', background: 'rgba(244, 63, 94, 0.12)', border: '1px solid rgba(244, 63, 94, 0.3)', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              ZYNEX NINJA PIPELINE
            </span>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
              CONTENT PLANNING & MAPPING ENGINE
            </span>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
              MULTI-TIER KEYWORD INTEGRATION
            </span>
          </div>

          <h1 style={{ fontSize: '30px', color: '#ffffff', fontWeight: 800, margin: '0 0 10px 0', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            Content Planning & Keyword Architecture Hub
          </h1>

          <p style={{ fontSize: '14.5px', color: '#94a3b8', margin: 0, maxWidth: '680px', lineHeight: 1.6 }}>
            Map out website content strategy line-by-line. Create new projects or import finalized keyword research and audit projects to map Primary, Secondary, and Tertiary keywords along with their exact variations across all planned service and landing pages.
          </p>
        </div>

        {/* Primary CTA Action */}
        <div>
          <button
            onClick={handleOpenModal}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '14px 24px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(2, 132, 199, 0.35)',
              transition: 'all 0.15s ease',
            }}
          >
            <Plus size={18} />
            <span>Start Content Plan</span>
          </button>
        </div>
      </div>

      {/* 2. STATS CARDS */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '16px',
          marginBottom: '28px',
        }}
      >
        <div
          style={{
            background: '#070c16',
            border: '1px solid #1e293b',
            borderRadius: '12px',
            padding: '20px',
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Layers size={14} color="#38bdf8" />
            <span>ACTIVE PROJECTS</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff' }}>
            {totalProjects}
          </div>
        </div>

        <div
          style={{
            background: '#070c16',
            border: '1px solid #1e293b',
            borderRadius: '12px',
            padding: '20px',
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FolderTree size={14} color="#10b981" />
            <span>PLANNED PAGES</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff' }}>
            {totalPagesPlanned}
          </div>
        </div>

        <div
          style={{
            background: '#070c16',
            border: '1px solid #1e293b',
            borderRadius: '12px',
            padding: '20px',
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Tag size={14} color="#f59e0b" />
            <span>KEYWORDS & VARIATIONS</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff' }}>
            {totalKeywordsTracked}
          </div>
        </div>
      </div>

      {/* 3. SEARCH & BAR */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 300px', maxWidth: '400px' }}>
          <SearchIcon size={16} color="#64748b" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search projects by name, URL, location, category..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px 10px 38px',
              borderRadius: '10px',
              background: '#070c16',
              border: '1px solid #1e293b',
              color: '#ffffff',
              fontSize: '13.5px',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* 4. PROJECTS LIST */}
      {filteredProjects.length === 0 ? (
        <div
          style={{
            background: '#070c16',
            border: '1px solid #1e293b',
            borderRadius: '16px',
            padding: '48px 24px',
            textAlign: 'center',
          }}
        >
          <FileText size={42} color="#334155" style={{ margin: '0 auto 16px auto' }} />
          <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: '0 0 8px 0' }}>
            No Content Planning Projects Found
          </h3>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px 0' }}>
            Create a new content plan or link an existing project from Keyword Research or Audit modules to get started.
          </p>
          <Button onClick={handleOpenModal} icon={<Plus size={16} />}>
            Create Content Plan
          </Button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {filteredProjects.map(proj => {
            const pageCount = proj.pages?.length || 0;
            const kwCount = (proj.pages || []).reduce((acc, p) => {
              let cnt = 0;
              if (p.primaryKeyword) cnt += 1 + (p.primaryKeywordVariations?.length || 0);
              if (p.secondaryKeyword) cnt += 1 + (p.secondaryKeywordVariations?.length || 0);
              if (p.tertiaryKeyword) cnt += 1 + (p.tertiaryKeywordVariations?.length || 0);
              return acc + cnt;
            }, 0);

            return (
              <div
                key={proj.id}
                style={{
                  background: '#070c16',
                  border: '1px solid #1e293b',
                  borderRadius: '14px',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                }}
              >
                <div>
                  {/* Top Badges & Options */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {proj.linkedKeywordProjectId && (
                        <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '2px 8px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Search size={11} /> Linked KW Research
                        </span>
                      )}
                      {proj.linkedAuditId && (
                        <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '2px 8px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <ShieldCheck size={11} /> Linked Audit
                        </span>
                      )}
                      {!proj.linkedKeywordProjectId && !proj.linkedAuditId && (
                        <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#94a3b8', background: 'rgba(148, 163, 184, 0.1)', border: '1px solid rgba(148, 163, 184, 0.2)', padding: '2px 8px', borderRadius: '6px' }}>
                          Standalone Project
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => onDeleteProject(proj.id)}
                      title="Delete Project"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#64748b',
                        cursor: 'pointer',
                        padding: '4px',
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Title & URL */}
                  <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>
                    {proj.clientName}
                  </h3>

                  <div style={{ fontSize: '12.5px', color: '#38bdf8', margin: '0 0 14px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Globe size={13} color="#38bdf8" />
                    <a href={proj.websiteUrl} target="_blank" rel="noreferrer" style={{ color: '#38bdf8', textDecoration: 'none' }}>
                      {proj.websiteUrl}
                    </a>
                  </div>

                  {/* Metadata Row */}
                  <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#94a3b8', marginBottom: '18px', flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={13} color="#f43f5e" />
                      {proj.targetedLocation || 'Global'}
                    </span>
                    <span>•</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Tag size={13} color="#f59e0b" />
                      {proj.category || 'General'}
                    </span>
                  </div>

                  {/* Highlights Summary */}
                  <div
                    style={{
                      background: '#090e1a',
                      border: '1px solid #1e293b',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      marginBottom: '20px',
                      fontSize: '12.5px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: '#94a3b8', fontWeight: 600 }}>Planned Pages:</span>
                      <span style={{ color: '#ffffff', fontWeight: 700 }}>{pageCount} Pages</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8', fontWeight: 600 }}>Keywords & Variations:</span>
                      <span style={{ color: '#38bdf8', fontWeight: 700 }}>{kwCount} Terms</span>
                    </div>
                  </div>
                </div>

                {/* Open Workspace Action */}
                <button
                  onClick={() => onSelectProject(proj.id)}
                  style={{
                    width: '100%',
                    padding: '11px 16px',
                    borderRadius: '10px',
                    background: 'rgba(2, 132, 199, 0.15)',
                    border: '1px solid rgba(56, 189, 248, 0.3)',
                    color: '#38bdf8',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span>Open Content Workspace</span>
                  <ArrowRight size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* 5. CREATE / LINK PROJECT MODAL */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#090e1a',
              border: '1px solid #1e293b',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '560px',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '28px',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                Create Content Planning Project
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '20px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            {/* Source Mode Toggle */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>
                Select Creation Source
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setSourceMode('keyword');
                    if (kwProjects.length > 0) handleKwSelect(kwProjects[0].id);
                  }}
                  style={{
                    padding: '10px 8px',
                    borderRadius: '8px',
                    border: sourceMode === 'keyword' ? '1px solid #0284c7' : '1px solid #1e293b',
                    background: sourceMode === 'keyword' ? 'rgba(2, 132, 199, 0.15)' : '#070c16',
                    color: sourceMode === 'keyword' ? '#ffffff' : '#94a3b8',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  Link KW Research
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setSourceMode('audit');
                    if (audits.length > 0) handleAuditSelect(audits[0].id);
                  }}
                  style={{
                    padding: '10px 8px',
                    borderRadius: '8px',
                    border: sourceMode === 'audit' ? '1px solid #0284c7' : '1px solid #1e293b',
                    background: sourceMode === 'audit' ? 'rgba(2, 132, 199, 0.15)' : '#070c16',
                    color: sourceMode === 'audit' ? '#ffffff' : '#94a3b8',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  Link Audit
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setSourceMode('new');
                    setClientName('');
                    setWebsiteUrl('');
                    setTargetedLocation('');
                    setCategory('');
                  }}
                  style={{
                    padding: '10px 8px',
                    borderRadius: '8px',
                    border: sourceMode === 'new' ? '1px solid #0284c7' : '1px solid #1e293b',
                    background: sourceMode === 'new' ? 'rgba(2, 132, 199, 0.15)' : '#070c16',
                    color: sourceMode === 'new' ? '#ffffff' : '#94a3b8',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  Create New
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* If linking Keyword Research */}
              {sourceMode === 'keyword' && (
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                    Select Keyword Research Project
                  </label>
                  {kwProjects.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#f43f5e', padding: '8px', background: 'rgba(244,63,94,0.1)', borderRadius: '6px' }}>
                      No Keyword Research projects available.
                    </div>
                  ) : (
                    <select
                      value={selectedKwId}
                      onChange={e => handleKwSelect(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        background: '#070c16',
                        border: '1px solid #1e293b',
                        color: '#ffffff',
                        fontSize: '13.5px',
                      }}
                    >
                      {kwProjects.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.clientName} ({p.websiteUrl})
                        </option>
                      ))}
                    </select>
                  )}
                  <p style={{ fontSize: '11.5px', color: '#10b981', marginTop: '6px', marginBottom: 0 }}>
                    ✨ Auto-populates finalized pages, primary/secondary/tertiary keywords & variations.
                  </p>
                </div>
              )}

              {/* If linking Audit */}
              {sourceMode === 'audit' && (
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                    Select Audit Instance
                  </label>
                  {audits.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#f43f5e', padding: '8px', background: 'rgba(244,63,94,0.1)', borderRadius: '6px' }}>
                      No Audit projects available.
                    </div>
                  ) : (
                    <select
                      value={selectedAuditId}
                      onChange={e => handleAuditSelect(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        background: '#070c16',
                        border: '1px solid #1e293b',
                        color: '#ffffff',
                        fontSize: '13.5px',
                      }}
                    >
                      {audits.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.clientName} ({a.websiteUrl})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Project Name */}
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                  Project Name / Client Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Apex Mechanical Services"
                  value={clientName}
                  onChange={e => setClientName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#070c16',
                    border: '1px solid #1e293b',
                    color: '#ffffff',
                    fontSize: '13.5px',
                  }}
                />
              </div>

              {/* Website URL */}
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                  Website URL <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="url"
                  required
                  placeholder="https://example.com"
                  value={websiteUrl}
                  onChange={e => setWebsiteUrl(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#070c16',
                    border: '1px solid #1e293b',
                    color: '#ffffff',
                    fontSize: '13.5px',
                  }}
                />
              </div>

              {/* Targeted Location */}
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                  Targeted Location <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Sydney, NSW or London, UK or Trinidad"
                  value={targetedLocation}
                  onChange={e => setTargetedLocation(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#070c16',
                    border: '1px solid #1e293b',
                    color: '#ffffff',
                    fontSize: '13.5px',
                  }}
                />
              </div>

              {/* Category */}
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                  Category / Industry <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Plumbing Services, Truck Parts, HVAC & Air Conditioning"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#070c16',
                    border: '1px solid #1e293b',
                    color: '#ffffff',
                    fontSize: '13.5px',
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
                <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Start Content Plan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
